// Tiny export helpers used by every report.  Zero npm deps — we lean on
// the browser primitives (Blob + window.print) and a couple of well-known
// tricks (HTML-as-.doc, CSV-as-Excel) so the VPS doesn't need to
// `npm install` anything new.
//
// Public API:
//   downloadCSV(filename, headers, rows)
//   downloadHtmlAsDoc(filename, htmlBody, title)
//   downloadHtmlAsPdf(htmlBody, title)        // opens print dialog → Save as PDF
//   printNode(node)                            // print just a DOM node
//   exportAs(format, opts)                     // one-call dispatcher

// ── CSV (opens in Excel / Numbers / Google Sheets) ──────────────────────
//   downloadCSV("guests.csv", ["Name","Email"], [["Ama","a@x"],…])
export function downloadCSV(filename, headers, rows) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    // Quote when the field contains anything CSV-special.
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [];
  if (Array.isArray(headers) && headers.length) lines.push(headers.map(esc).join(","));
  for (const row of rows || []) lines.push((row || []).map(esc).join(","));
  // BOM so Excel handles UTF-8 (Cedi symbol, accents) correctly.
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}

// ── HTML rendered as a Word .doc file ───────────────────────────────────
// Microsoft Word and LibreOffice happily open an HTML file that's labelled
// as application/msword + .doc.  The fidelity is excellent for tables and
// basic formatting which is exactly what reports need.
export function downloadHtmlAsDoc(filename, htmlBody, title = "Report") {
  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <!--[if gte mso 9]>
      <xml>
        <w:WordDocument>
          <w:View>Print</w:View>
          <w:Zoom>90</w:Zoom>
          <w:DoNotPromptForConvert/>
        </w:WordDocument>
      </xml>
    <![endif]-->
    <style>
      body  { font-family: 'Calibri', 'Helvetica', sans-serif; color: #2c1f14; }
      h1    { font-family: 'Cambria', Georgia, serif; font-size: 22pt; }
      table { width: 100%; border-collapse: collapse; margin: 12px 0; }
      th    { background: #2c1f14; color: #f0e4cf; padding: 6px; text-align: left; font-size: 10pt; }
      td    { border-bottom: 1px solid #ddd; padding: 6px; font-size: 10pt; }
      .kpi  { display: inline-block; margin-right: 24px; }
      .kpi b{ font-size: 18pt; display: block; }
    </style>
  </head>
  <body>${htmlBody}</body>
</html>`;
  const blob = new Blob([html], { type: "application/msword" });
  triggerDownload(blob, filename.endsWith(".doc") ? filename : `${filename}.doc`);
}

// ── PDF via the browser's print pipeline ───────────────────────────────
// Opens a popup window with the report HTML + the print stylesheet, then
// fires window.print() which gives the user "Save as PDF" (every modern
// browser supports it).  No external PDF dep needed.
export function downloadHtmlAsPdf(htmlBody, title = "Report") {
  openPrintWindow(htmlBody, title);
}

// ── Native print of an in-page DOM node ────────────────────────────────
// Clones the node into a popup so we don't fight the live React tree.
export function printNode(node, title = "Report") {
  if (!node) return;
  openPrintWindow(node.outerHTML, title);
}

// ── Format dispatcher used by the report shell's action buttons ─────────
// opts:
//   - node     : DOM node to print (for "print" / "pdf")
//   - htmlBody : HTML string to bake into the .doc / .pdf file
//   - title    : window / file title
//   - filename : base filename (no extension)
//   - csv      : { headers: [...], rows: [...] } for "excel"
export function exportAs(format, opts = {}) {
  const { node, htmlBody, title = "Report", filename = "report", csv } = opts;
  switch (format) {
    case "print":
      printNode(node, title); break;
    case "pdf":
      downloadHtmlAsPdf(htmlBody || (node ? node.outerHTML : ""), title); break;
    case "word":
    case "doc":
      downloadHtmlAsDoc(filename, htmlBody || (node ? node.outerHTML : ""), title); break;
    case "excel":
    case "csv":
      if (csv) downloadCSV(filename, csv.headers, csv.rows);
      break;
    default:
      console.warn("Unknown export format:", format);
  }
}

// ── Internals ───────────────────────────────────────────────────────────

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function openPrintWindow(htmlBody, title) {
  // Inline a print stylesheet so the popup looks like a clean A4 report
  // even when the user chooses "Save as PDF".  Avoids dragging in the
  // app's full CSS (which has fixed positioning, dark sidebars, etc.).
  const css = `
    @page { size: A4; margin: 18mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #2c1f14; margin: 0; }
    h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 28px; margin: 0 0 4px; }
    h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 20px; margin: 16px 0 6px; color: #6b5142; }
    .report-meta { color: #6b5142; font-size: 12px; margin-bottom: 18px; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0 20px; font-size: 12px; }
    thead th { background: #2c1f14; color: #f0e4cf; text-align: left; padding: 8px; font-weight: 500; letter-spacing: .04em; text-transform: uppercase; font-size: 11px; }
    tbody td { border-bottom: 1px solid #e5d8c4; padding: 7px 8px; }
    .kpi-row { display: flex; flex-wrap: wrap; gap: 16px; margin: 10px 0 18px; }
    .kpi { border: 1px solid #d8c4ad; border-radius: 6px; padding: 10px 14px; min-width: 130px; }
    .kpi-label { font-size: 10px; letter-spacing: .15em; text-transform: uppercase; color: #6b5142; }
    .kpi-value { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 22px; color: #2c1f14; margin-top: 2px; }
    .pie-legend, .bar-chart { font-size: 12px; }
    .no-print, .report-actions { display: none !important; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  `;
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>${escapeHtml(title)}</title><style>${css}</style></head>
<body>${htmlBody}</body></html>`;
  const w = window.open("", "_blank", "width=900,height=900");
  if (!w) {
    alert("Pop-up blocked. Please allow pop-ups to export / print.");
    return;
  }
  w.document.write(html);
  w.document.close();
  // Wait for layout + any external font load before triggering print.
  w.onload = () => setTimeout(() => { w.focus(); w.print(); }, 300);
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;",
  }[c]));
}
